import { createContext, useContext, type MouseEvent, type ReactNode } from 'react'
import type { CommerceRecommendationContext } from 'src/shared/commerceAnalytics'
import { trackRecommendationClick } from 'src/functions/commerceTracking'

const RecommendationTrackingContext = createContext<CommerceRecommendationContext | undefined>(undefined)
export const useRecommendationTracking = () => useContext(RecommendationTrackingContext)
export const RecommendationTrackingProvider = RecommendationTrackingContext.Provider

const homepageContext: CommerceRecommendationContext = { surface: 'home', section: 'homepage' }

/** Covers both catalogue cards and authored homepage links to actual products.
 * Navigation, category tiles and campaign links are intentionally excluded. */
export const HomepageProductTracking = ({ children }: { children: ReactNode }) => {
  const trackLink = (event: MouseEvent<HTMLDivElement>) => {
    // Next Link prevents the browser navigation before this bubble handler.
    // Explicit card gestures stop propagation; normal client navigation counts.
    if (![0, 1].includes(event.button)) return
    try {
      const anchor = event.target instanceof Element ? event.target.closest('a[href]') : null
      if (!anchor || !event.currentTarget.contains(anchor)) return
      const url = new URL(anchor.getAttribute('href') || '', window.location.href)
      if (url.origin !== window.location.origin) return
      const match = /^\/p\/[^/]+\/(\d+)(?:\/([^/]+))?\/?$/.exec(url.pathname)
      if (!match) return
      const id = Number(match[1])
      const key = match[2] ? decodeURIComponent(match[2]) : undefined
      trackRecommendationClick(event.nativeEvent, { id, ...(key ? { key } : {}) }, homepageContext)
    } catch { /* Invalid authored links do not interrupt navigation. */ }
  }
  return <RecommendationTrackingProvider value={homepageContext}>
    <div style={{ display: 'contents' }} data-commerce-surface='home' onClick={trackLink} onAuxClick={event => { if (event.button === 1) trackLink(event) }}>
      {children}
    </div>
  </RecommendationTrackingProvider>
}
